import React, { useState } from 'react'
import { IonItem, IonLabel, IonList, IonMenuToggle, IonText } from '@ionic/react'
import { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { getAllPCRDocs } from '../../../features/home/store/home.actions'
import { getPcrSelector } from '../../../features/home/selectors'
import { AuthenticationService } from '../../../features/login/services/authentication.service'
import { saveRuleErrors } from '../../../features/rules/store/rules.actions'
import { sessionPersistService } from '../../../features/session/persist/service/sessionPersist.service'
import { getCustomSignaturesSelector } from '../../../features/customSignature/selectors/getCustomSignature.selectors'
import { getSignaturesSelector } from '../../../features/signatures/selectors/getSignatures.selector'
import { setSegment } from '../../../features/timeline/store/timeline.actions'
import { getUserSelector } from '../../../features/user/selectors'
import { IRootState } from '../../../store'
import { clearLocalNotifications } from '../../../utilities/localNotifications.util'
import { hasCustomSignatureUnsavedData, hasStandardSignatureUnsavedData } from '../../../utilities/signature.util'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import { AppRouterConstants } from '../../appRouter/AppRouter.constants'
import { TabConstants } from '../../tabs/Tabs.constants'
import './PcrMenuContent.content.css'

type PcrMenuItemId = 'shiftStartup' | 'chartSummary' | 'pcrDetails' | 'helpGuide' | 'supportTicket' | 'logout'

interface IPcrMenuItem {
  id: PcrMenuItemId
  label: string
  disabled?: boolean
  onClick: (e: BaseSyntheticEvent) => void
}

interface IPcrMenuContentProps {
  onCloseMenu: () => void
  onOpenSupportTicket: () => void
}

const PcrMenuContent: React.FC<IPcrMenuContentProps> = ({ onCloseMenu, onOpenSupportTicket }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const user = useSelector(getUserSelector)
  const { userName, userId, serviceName, serviceId } = user
  const pcr = useSelector(getPcrSelector)
  const isOfflineStatus = useOfflineStatus()
  const [showPcrDetails, setShowPcrDetails] = useState(false)
  const submitChartState = useSelector((state: IRootState) => state.submitChart)
  const { prid, success } = submitChartState
  const timelineSegment = useSelector((state: IRootState) => state.timeline.segment)
  const customSignaturesState = useSelector(getCustomSignaturesSelector)
  const { customSignatureClientData } = customSignaturesState
  const signaturesState = useSelector(getSignaturesSelector)
  const isNavigationDisabled =
    hasStandardSignatureUnsavedData(signaturesState) || hasCustomSignatureUnsavedData(customSignatureClientData)

  const pathname = history.location?.pathname ?? ''

  const activeItemId: PcrMenuItemId | null = (() => {
    if (pathname.startsWith(AppRouterConstants.CREW_ROUTE)) return 'shiftStartup'
    if (pathname.startsWith(TabConstants.TIMELINE_TAB) && timelineSegment === t('timeline.Summary')) {
      return 'chartSummary'
    }
    if (pathname.startsWith(AppRouterConstants.HELP_ROUTE)) return 'helpGuide'
    if (showPcrDetails) return 'pcrDetails'
    return null
  })()

  const shiftClick = (e: BaseSyntheticEvent) => {
    if (!isNavigationDisabled) {
      onCloseMenu()
      e.preventDefault()
      dispatch(saveRuleErrors())
      dispatch(getAllPCRDocs())
      clearLocalNotifications()
      history.push(AppRouterConstants.CREW_ROUTE)
    }
  }

  const chartSummaryClick = (e: BaseSyntheticEvent) => {
    if (!isNavigationDisabled) {
      onCloseMenu()
      e.preventDefault()
      dispatch(setSegment(t('timeline.Summary')))
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const pcrDetailsClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    setShowPcrDetails((expanded) => !expanded)
  }

  const helpGuideOnClick = (e: BaseSyntheticEvent) => {
    if (!isNavigationDisabled && !isOfflineStatus) {
      onCloseMenu()
      e.preventDefault()
      history.push(AppRouterConstants.HELP_ROUTE, history.location)
    }
  }

  const supportTicketOnClick = (e: BaseSyntheticEvent) => {
    if (!isOfflineStatus) {
      e.preventDefault()
      onOpenSupportTicket()
    }
  }

  const logoutOnClick = (e: BaseSyntheticEvent) => {
    if (!isNavigationDisabled) {
      onCloseMenu()
      e.preventDefault()
      dispatch(saveRuleErrors())
      clearLocalNotifications()
      AuthenticationService.doLogout(user, dispatch)
      history.push(AppRouterConstants.INDEX_ROUTE)
      sessionPersistService.clearMetadata()
    }
  }

  const renderPcrDetails = () => {
    if (!showPcrDetails || (!pcr?.pcrId && !prid)) {
      return null
    }

    return (
      <div className='pcr-menu-details'>
        {pcr?.pcrId && !success && <IonText>Local PRID : {pcr.pcrId}</IonText>}
        {prid && <IonText>Remote PRID : {prid}</IonText>}
      </div>
    )
  }

  const getConditionalMenuItems = (): IPcrMenuItem[] => {
    const items: IPcrMenuItem[] = []

    if (pcr && !success) {
      items.push({
        id: 'chartSummary',
        label: t('common.ChartSummary'),
        disabled: isNavigationDisabled,
        onClick: chartSummaryClick,
      })
    }

    if (pcr) {
      items.push({
        id: 'pcrDetails',
        label: t('common.PCRDetails'),
        onClick: pcrDetailsClick,
      })
    }

    return items
  }

  const menuItems: IPcrMenuItem[] = [
    {
      id: 'shiftStartup',
      label: t('shift.ShiftStartup'),
      disabled: isNavigationDisabled,
      onClick: shiftClick,
    },
    ...getConditionalMenuItems(),
    {
      id: 'helpGuide',
      label: t('common.buttons.Help'),
      disabled: isNavigationDisabled || isOfflineStatus,
      onClick: helpGuideOnClick,
    },
    {
      id: 'supportTicket',
      label: t('zollSupportTicket.CreateZOLLSupportTicket'),
      disabled: isOfflineStatus,
      onClick: supportTicketOnClick,
    },
    {
      id: 'logout',
      label: t('common.buttons.Logout'),
      disabled: isNavigationDisabled,
      onClick: logoutOnClick,
    },
  ]

  const renderMenuItem = (item: IPcrMenuItem) => {
    const itemClassName = [
      'pcr-menu-item',
      item.id === activeItemId ? 'pcr-menu-item--active' : '',
      item.disabled ? 'pcr-menu-item--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ')

    const menuItem = (
      <IonItem
        lines='none'
        button
        disabled={item.disabled}
        detail={false}
        className={itemClassName}
        onClick={item.onClick}
        data-testid={`PcrMenuItem-${item.id}`}>
        <IonLabel className='pcr-menu-item-label'>{item.label}</IonLabel>
      </IonItem>
    )

    return (
      <React.Fragment key={item.id}>
        {item.id === 'logout' && <hr className='pcr-menu-divider' />}
        {item.id === 'supportTicket' ? <IonMenuToggle>{menuItem}</IonMenuToggle> : menuItem}
        {item.id === 'pcrDetails' && renderPcrDetails()}
      </React.Fragment>
    )
  }

  return (
    <div className='pcr-menu'>
      <div className='pcr-menu-user-info'>
        <IonText>
          {userName} ({userId})
        </IonText>
        <IonText>
          {serviceName} ({serviceId})
        </IonText>
      </div>
      <IonList lines='full'>{menuItems.map(renderMenuItem)}</IonList>
    </div>
  )
}

export default PcrMenuContent
